import React from "react";
import { shallowEqual } from "react-redux";
import { CircleMarker, Polyline, Popup, useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "leaflet-arrowheads";

import { useAppSelector } from "../../redux/hooks";
import { concatenateBuses, dynamicTextColor } from "../../utils/helpers";
import MapBusesComponent from "../MapBusesComponent";
import variableColors from "../../_App.module.scss";
import { getWheelColors } from "../../utils/helperWheelColors";
import { stopsOptionsConstant } from "../../data/constants";
import SelectedBusViewMapLegend from "./SelectedBusViewMapLegend";
import { StopAvailableRoutes } from "../helpers/Helpers";
import { AgencyContext } from "../../hooks/AgencyContext";
import { statuteMile } from "../../config";
// import SelectedBusViewMapProfile from "./SelectedBusViewMapProfile"; //^ In DevMode only - For testing profile only!!

const { customSecondaryColor } = variableColors;

export const PopupContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-content: center;
  gap: 0.25rem;
  div.popup-line {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    align-content: center;
    gap: 0.5rem;
    width: 100%;
    span {
      font-weight: bold;
    }
    span.span_maroon {
      color: ${customSecondaryColor};
    }
  }
`;

const SelectedBusViewMap = ({
  showLineNumbers,
  showDeviations,
}: {
  showLineNumbers: boolean;
  showDeviations: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const leafletMap = useMap();
  const polylineRef = React.useRef(null) as React.MutableRefObject<any>;

  //* AgencyContext
  const { speedUnit } = React.useContext(AgencyContext) as AgencyContextType;

  const [busesFromRedux, currentTripDataFromRedux, currentBusId]: [BusesSets, CurrentTripData, string] = useAppSelector(
    (state: RootState) => [state?.buses, state?.currentTripData?.currentTripData, state?.currentTripData?.currentBusId],
    shallowEqual
  );

  const [currentBusData, setCurrentBusData] = React.useState<BusInfo>({} as BusInfo);
  const [tripStops, setTripStops] = React.useState<Array<Schedule>>([]);
  const [tripShape, setTripShape] = React.useState<Array<LanLngPoint>>([]);

  React.useEffect(() => {
    if (busesFromRedux && currentTripDataFromRedux && currentBusId) {
      const allBuses = concatenateBuses(busesFromRedux);
      const currentSelectedBus = allBuses?.filter((bus: BusInfo) => bus.id === currentBusId)[0];
      setCurrentBusData(currentSelectedBus);

      const currentTripPath = currentTripDataFromRedux?.stopPaths;
      const currentTripStops = currentTripDataFromRedux?.schedule;
      setTripShape(currentTripPath);
      setTripStops(currentTripStops);
    }
  }, [busesFromRedux, currentBusId, currentTripDataFromRedux]);

  const ContainerPopup = (): JSX.Element => {
    const { routeInfo } = currentTripDataFromRedux;
    return (
      <PopupContainer>
        <div className="popup-line">
          {t("Id")}{" "}
          <span
            style={{
              background: routeInfo?.color,
              color: dynamicTextColor(routeInfo?.color) || "#000000",
              padding: "0.25rem 0.5rem",
            }}
          >
            {routeInfo?.shortName || routeInfo?.id || t("b/d")}
          </span>
        </div>
        <div className="popup-line">
          {t("Nazwa")} <span className="span_maroon">{routeInfo?.longName || t("b/d")}</span>
        </div>
        <div className="popup-line">
          {t("Dokąd")}: <span className="span_maroon"> {currentTripDataFromRedux?.headsign || t("b/d")}</span>
        </div>
        <div className="popup-line">
          {t("Długość")}:{" "}
          <span className="span_maroon">
            {speedUnit === "kmh"
              ? `${(currentTripDataFromRedux?.pathLength / 1000).toFixed(2)} km`
              : speedUnit === "mph"
              ? `${(currentTripDataFromRedux?.pathLength / (1000 * statuteMile)).toFixed(2)} miles`
              : t("b/d")}
          </span>
        </div>
        <div className="popup-line">
          {t("Kierunek")}: <span className="span_maroon">{currentTripDataFromRedux?.directionId || t("b/d")}</span>
        </div>
        <div className="popup-line">
          {t("Kalendarz")}: <span className="span_maroon">{currentTripDataFromRedux?.serviceId || t("b/d")}</span>
        </div>
        <div className="popup-line  small text-muted">
          {t("Shape")}: <span className="span_maroon">{currentTripDataFromRedux?.tripPattern || t("b/d")}</span>
        </div>
      </PopupContainer>
    );
  };

  //* ArrowHeads Polyline
  React.useEffect(() => {
    if (!leafletMap) return;
    if (
      leafletMap &&
      currentTripDataFromRedux &&
      Object.keys(currentTripDataFromRedux)?.length &&
      tripShape &&
      tripShape?.length
    ) {
      const polyline = polylineRef?.current;
      if (polyline) {
        polyline?.arrowheads({
          yawn: 45,
          size: "10px",
          frequency: 6,
          fill: false,
          offsets: {
            start: "100px",
            end: "100px",
          },
          color: getWheelColors(currentTripDataFromRedux?.routeInfo.color, 3)?.[0],
        });
        polyline?._update();
      }
    }
  }, [currentTripDataFromRedux, leafletMap, tripShape]);

  const TripStops = (): JSX.Element => {
    return (
      <React.Fragment>
        {tripStops &&
          tripStops?.length >= 1 &&
          (tripStops?.map((elem: Schedule, index: number) => (
            <CircleMarker
              pathOptions={{
                ...stopsOptionsConstant,
                color: getWheelColors(currentTripDataFromRedux?.routeInfo.color, 3)?.[1],
                fillColor: getWheelColors(currentTripDataFromRedux?.routeInfo.color, 3)?.[2],
              }}
              key={String(index + elem?.stopId + elem?.stopName)}
              center={{
                lat: elem?.position?.lat as number,
                lng: elem?.position?.lon as number,
              }}
              radius={6}
            >
              <Popup>
                <PopupContainer>
                  <div className="popup-line">
                    {t("Nazwa")} <span className="span_maroon">{elem?.stopName}</span>
                  </div>
                  <div className="popup-line">
                    {t("Id")} <span className="span_maroon">{elem?.stopId}</span>
                  </div>
                  <div className="popup-line text-muted small">
                    {t("Pozycja")}:{" "}
                    <span className="span_bold text-muted">{`${elem?.position?.lat} ${elem?.position?.lon}`}</span>
                  </div>
                  <StopAvailableRoutes stopId={elem?.stopId} />
                </PopupContainer>
              </Popup>
            </CircleMarker>
          )) as JSX.Element[])}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {currentBusData && Object.keys(currentBusData)?.length ? (
        <MapBusesComponent
          showLineNumbers={showLineNumbers}
          busesOnMap={[currentBusData]}
          showDeviations={showDeviations}
          moreActions={true}
        />
      ) : null}

      {currentTripDataFromRedux && Object.keys(currentTripDataFromRedux).length && tripShape && tripShape?.length ? (
        <Polyline
          ref={polylineRef}
          positions={tripShape}
          pathOptions={{ color: currentTripDataFromRedux?.routeInfo?.color, weight: 4 }}
          children={
            <Popup>
              <ContainerPopup />
            </Popup>
          }
        />
      ) : null}

      {currentTripDataFromRedux && Object.keys(currentTripDataFromRedux)?.length && tripStops && tripStops?.length
        ? TripStops()
        : null}

      {/* //* Legend */}
      {currentTripDataFromRedux &&
      Object.keys(currentTripDataFromRedux)?.length &&
      currentBusData &&
      Object.keys(currentBusData) ? (
        <SelectedBusViewMapLegend
          currentTripDataFromRedux={currentTripDataFromRedux}
          currentBusData={currentBusData}
          speedUnit={speedUnit as string}
        />
      ) : null}

      {/* //^ Route Profile Check */}
      {/* {process.env.NODE_ENV === "development" ? <SelectedBusViewMapProfile /> : null} */}
    </React.Fragment>
  );
};

export default SelectedBusViewMap;
